import { useState, useEffect, useMemo } from "react";
import { produce } from "immer";
import { t } from "i18next";

import Sidebar from "@components/Sidebar/Sidebar";
import Pill from "@components/Pill/Pill";
import RevertChanges from "./Modals/RevertChanges";
import TemporarilyClose from "./Modals/TemporarilyClose";
import Reopen from "./Modals/Reopen";
import DeleteFeature from "./Modals/DeleteFeature";
import DeleteImage from "./Modals/DeleteImage";
import OperatingHours from "./Modals/OperatingHours";
import { Tabs } from "./Tabs";
import type { InfoFieldErrors } from "./Tabs/Info/Info";
import { useDebounce } from "@hooks";
import { createTimeArray, setValueByPath } from "@utils";
import {
  FeatureData,
  OperatingHoursObject,
  Icon as IconType,
  CategoryObject,
  SubcategoryObject,
  STATUS,
  MediaObject,
} from "@redux/services/types";
import styles from "./EditFeatureSidebar.module.scss";
import CloseSidebar from "./Modals/CloseSidebar";
import { useUploadFeatureMediaForMapByIdMutation } from "@redux/services/mms";
import { useAppSelector } from "@redux/hooks";
import Unarchive from "./Modals/Unarchive";
import { Button, Dialog, Spinner } from "@livingmap/hippo";

type EditFeatureTabs = "info" | "media" | "tags" | "history";

type DeleteImageModalState = {
  isActive: boolean;
  file: MediaObject | null;
};

const tabs: EditFeatureTabs[] = ["info", "media", "tags", "history"];
const times = createTimeArray(5); // For the dropdown options in the operating hours modal

const MAX_FIELD_LENGTH = 100;

const isValidHttpUrl = (value: string) => {
  const urlValue = value.trim();
  const hasExplicitProtocol = /^https?:\/\//i.test(urlValue);

  if (!hasExplicitProtocol) {
    return false;
  }

  try {
    const url = new URL(urlValue);
    const hasAllowedProtocol =
      url.protocol === "http:" || url.protocol === "https:";
    return hasAllowedProtocol;
  } catch {
    return false;
  }
};

const createEmptyValidationErrors = (): InfoFieldErrors => ({
  labelName: {},
  labelReference: {},
  longName: {},
  summary: {},
  website: null,
});

const validateFeatureData = (
  feature: FeatureData,
  messages: {
    maxLength: string;
    invalidUrl: string;
  },
): InfoFieldErrors => {
  const errors = createEmptyValidationErrors();

  feature.label?.name?.forEach(({ lang, text }) => {
    if (text && text.length > MAX_FIELD_LENGTH) {
      errors.labelName[lang] = messages.maxLength;
    }
  });

  feature.label?.reference?.forEach(({ lang, text }) => {
    if (text && text.length > MAX_FIELD_LENGTH) {
      errors.labelReference[lang] = messages.maxLength;
    }
  });

  feature.information?.long_name?.forEach(({ lang, text }) => {
    if (text && text.length > MAX_FIELD_LENGTH) {
      errors.longName[lang] = messages.maxLength;
    }
  });

  feature.information?.summary?.forEach(({ lang, text }) => {
    if (text && text.length > MAX_FIELD_LENGTH) {
      errors.summary[lang] = messages.maxLength;
    }
  });

  const websiteUrl = (feature.information?.links?.[0]?.url ?? "").trim();
  errors.website =
    websiteUrl && !isValidHttpUrl(websiteUrl) ? messages.invalidUrl : null;

  return errors;
};

interface Props extends FeatureData {
  isLoading: boolean;
  icons: IconType[];
  allCategories: CategoryObject[];
  onSidebarCloseClick: () => void;
  onEditFeatureSave: (data: FeatureData) => void;
  onFeatureDelete: (data: FeatureData) => void;
  onUnarchiveFeature: (data: FeatureData) => void;
  onFeatureSidebarReady?: () => void;
  onRevertAllChanges: () => void;
  onDataChanged: (hasDataChanged: boolean) => void;
  children?: React.ReactNode;
}

const EditFeatureSidebar: React.FC<Props> = ({
  isLoading,
  icons,
  allCategories,
  onSidebarCloseClick,
  onEditFeatureSave,
  onFeatureDelete,
  onUnarchiveFeature,
  onFeatureSidebarReady,
  onRevertAllChanges,
  onDataChanged,
  children,
  ...props
}) => {
  const { mapID, featureID } = useAppSelector((state) => state.application);
  const maxLengthErrorMessage = t("validation.max_characters", {
    count: MAX_FIELD_LENGTH,
    defaultValue: `Must be ${MAX_FIELD_LENGTH} characters or fewer`,
  });
  const invalidUrlErrorMessage = t("validation.invalid_url", {
    defaultValue: "Enter a valid URL (including http/https)",
  });
  const [activeTab, setActiveTab] = useState<EditFeatureTabs>("info");
  const [isRevertModalActive, setIsRevertModalActive] = useState(false);
  const [isTemporarilyCloseModalActive, setIsTemporarilyCloseModalActive] =
    useState(false);
  const [isReopenModalActive, setIsReopenModalActive] = useState(false);
  const [isDeleteFeatureModalActive, setIsDeleteFeatureModalActive] =
    useState(false);
  const [isUnarchiveModalActive, setIsUnarchiveModalActive] = useState(false);
  const [isOperatingHoursModalActive, setIsOperatingHoursModalActive] =
    useState(false);
  const [deleteImageModalState, setDeleteImageModalState] =
    useState<DeleteImageModalState>({
      isActive: false,
      file: null,
    });
  const [isCloseSidebarModalActive, setCloseSidebarModalActive] =
    useState(false);
  const [dataHasChanged, setDataHasChanged] = useState(false);

  const [data, setData] = useState<FeatureData>(props);
  const [validationErrors, setValidationErrors] = useState<InfoFieldErrors>(
    () =>
      validateFeatureData(props, {
        maxLength: maxLengthErrorMessage,
        invalidUrl: invalidUrlErrorMessage,
      }),
  );
  const [uploadMedia] = useUploadFeatureMediaForMapByIdMutation();

  useEffect(() => {
    onFeatureSidebarReady && onFeatureSidebarReady();
  }, [onFeatureSidebarReady]);

  const jsonProps = JSON.stringify(props);
  const jsonData = JSON.stringify(data);

  const debouncedDataHasChanged = useDebounce(jsonProps !== jsonData, 100);

  // Ensures local state is updated if any prop values change i.e. once "save" has been clicked & we receive updated feature data from the API which gets passed in from the parent
  useEffect(() => {
    const parsedProps = JSON.parse(jsonProps) as FeatureData;
    setData(parsedProps);
  }, [jsonProps]); // JSON.stringify ensures deep comparison of the props object & that it only runs when a nested value changes

  // Set the local "dataHasChanged" state & call the "onDataChanged" callback, so that parent components can update the "hasUnsavedChanges" state in the redux store as required
  useEffect(() => {
    setDataHasChanged(debouncedDataHasChanged);
    onDataChanged(debouncedDataHasChanged);
  }, [debouncedDataHasChanged, onDataChanged]);

  useEffect(() => {
    setValidationErrors(
      validateFeatureData(data, {
        maxLength: maxLengthErrorMessage,
        invalidUrl: invalidUrlErrorMessage,
      }),
    );
  }, [data, maxLengthErrorMessage, invalidUrlErrorMessage]);

  const hasValidationErrors = useMemo(
    () =>
      Object.values(validationErrors).some((fieldErrors) => {
        if (!fieldErrors) return false;
        if (typeof fieldErrors === "string") {
          return Boolean(fieldErrors);
        }
        return Object.values(fieldErrors).some(Boolean);
      }),
    [validationErrors],
  );

  if (isLoading || !data.id || !allCategories || !icons) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner dataQA="loading-spinner" size={24} />
      </div>
    );
  }

  const changeHandler = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    path: string,
    defaultObject?: {
      [key: string]: any;
    },
  ) => {
    if (e.target.type === "checkbox") {
      const newData = produce(data, (draftState) => {
        if (!("checked" in e.target)) return;
        setValueByPath(draftState, path, e.target.checked);
      });
      return setData(newData);
    }

    const newData = produce(data, (draftState) => {
      setValueByPath(draftState, path, e.target.value || null, defaultObject);
    });
    setData(newData);
  };

  const handleIconChange = (icon: IconType) => {
    setData({
      ...data,
      label: {
        ...data.label,
        icon: icon.id,
      },
    });
  };

  const handleIconClear = () => {
    setData({
      ...data,
      label: {
        ...data.label,
        icon: null,
      },
    });
  };

  const handleCategorySelect = (category: CategoryObject) => {
    setData({
      ...data,
      categories: {
        category: {
          id: category.id,
          name: category.name,
        },
        subcategory: {
          id: category.subcategories[0].id,
          name: category.subcategories[0].name,
        },
      },
    });
  };

  const handleSubcategorySelect = (subcategory: SubcategoryObject) => {
    setData({
      ...data,
      categories: {
        ...data.categories,
        subcategory: {
          id: subcategory.id,
          name: subcategory.name,
        },
      },
    });
  };

  const handleRankChange = (value: number) => {
    setData({
      ...data,
      rank: {
        ...data.rank,
        value,
      },
    });
  };

  const handleRankReset = () => {
    setData({
      ...data,
      rank: {
        ...data.rank,
        value: data.rank.default,
      },
    });
  };

  const handleFileAdd = (file: File, type: "original" | "360") => {
    const formData = new FormData();
    formData.append("image", file);
    uploadMedia({ mapID, featureID, data: formData, type }).then((response) => {
      if ("error" in response) throw new Error("Error uploading media file");

      const mergedMedia = produce(data.media || [], (draft) => {
        const existingMedia = draft.find(
          (media) => media.id === response.data.id,
        );
        if (existingMedia) {
          Object.assign(existingMedia, response.data);
        } else {
          draft.push(response.data);
        }
      });

      setData({
        ...data,
        media: mergedMedia,
      });
    });
  };

  const handleFileDelete = () => {
    const { file, isActive } = deleteImageModalState;
    if (!isActive) return;

    if (data.media) {
      const updatedMedia = data.media.filter((media) => media.id !== file?.id);

      setData({
        ...data,
        media: updatedMedia,
      });
    }

    setDeleteImageModalState({
      isActive: false,
      file: null,
    });
  };

  const handleTagAdd = (tag: string, language: string) => {
    setData({
      ...data,
      tags: data.tags.map((languageObject) => {
        if (languageObject.lang === language) {
          return {
            ...languageObject,
            text: languageObject.text ? [...languageObject.text, tag] : [tag],
          };
        }
        return languageObject;
      }),
    });
  };

  const handleTagDelete = (tag: string, language: string) => {
    setData({
      ...data,
      tags: data.tags.map((languageObject) => {
        if (languageObject.lang === language) {
          return {
            ...languageObject,
            text: languageObject.text
              ? languageObject.text.filter((t) => t !== tag)
              : [],
          };
        }
        return languageObject;
      }),
    });
  };

  const handleOpenAndClose = (type: "closed" | "open") => {
    setIsTemporarilyCloseModalActive(false);
    setIsReopenModalActive(false);
    setData({
      ...data,
      is_temporarily_closed: type === "closed",
    });
  };

  const handleOperatingHoursUpdate = (operatingHours: OperatingHoursObject) => {
    setData({
      ...data,
      information: {
        ...data.information,
        operating_hours: operatingHours,
      },
    });
    setIsOperatingHoursModalActive(false);
  };

  const handleRevertAllChanges = () => {
    setData(props);
    setIsRevertModalActive(false);
    onRevertAllChanges();
  };

  const handleFeatureDelete = () => {
    setIsDeleteFeatureModalActive(false);
    onFeatureDelete(data);
  };

  const handleUnarchiveFeature = () => {
    setIsUnarchiveModalActive(false);
    onUnarchiveFeature(data);
  };

  let tabContent;

  switch (activeTab) {
    case "info":
      tabContent = (
        <Tabs.Info
          dataQA="info-tab"
          featureID={data.id}
          isTemporarilyClosed={data.is_temporarily_closed}
          onChange={changeHandler}
          onOperatingHoursClick={() => setIsOperatingHoursModalActive(true)}
          onTemporarilyCloseClick={() => setIsTemporarilyCloseModalActive(true)}
          onReopenClick={() => setIsReopenModalActive(true)}
          onDeleteClick={() => setIsDeleteFeatureModalActive(true)}
          onUnarchiveClick={() => setIsUnarchiveModalActive(true)}
          icons={icons}
          onIconChange={handleIconChange}
          onIconClearClick={handleIconClear}
          allCategories={allCategories}
          categories={data.categories}
          onCategorySelect={handleCategorySelect}
          onSubcategorySelect={handleSubcategorySelect}
          label={{ ...data.label }}
          rank={{
            minimum: data?.rank?.minimum,
            maximum: data?.rank?.maximum,
            value: data?.rank?.value,
            onChange: handleRankChange,
            onResetClick: handleRankReset,
          }}
          street_address={data.street_address}
          location={{ ...data.location }}
          information={{ ...data.information }}
          status={data?.status}
          fieldErrors={validationErrors}
        />
      );
      break;
    case "media":
      tabContent = (
        <Tabs.Media
          dataQA="media-tab"
          media={data.media}
          onFileAdd={(file: File) => handleFileAdd(file, "original")}
          onPanoramicFileAdd={(file: File) => handleFileAdd(file, "360")}
          onCameraPositionChange={(position) =>
            setData({
              ...data,
              media:
                data.media?.map((mediaObject) => {
                  if (mediaObject.id === "360") {
                    return {
                      ...mediaObject,
                      camera_position: position,
                    };
                  }
                  return mediaObject;
                }) || null,
            })
          }
          onDeleteClick={(file) =>
            setDeleteImageModalState({
              isActive: true,
              file,
            })
          }
          status={data?.status}
        />
      );
      break;
    case "tags":
      tabContent = (
        <Tabs.Tags
          dataQA="tags-tab"
          tags={data.tags}
          onTagAdd={handleTagAdd}
          onTagDelete={handleTagDelete}
          status={data?.status}
        />
      );
      break;
    case "history":
      tabContent = (
        <Tabs.History dataQA="history-tab" lastModified={data.last_modified} />
      );
      break;
    default:
  }

  const closeSidebarModal = () => (
    <Dialog
      dataQA="close-sidebar-dialog"
      isOpen={isCloseSidebarModalActive}
      onClose={() => setCloseSidebarModalActive(false)}
      footer={false}
    >
      <CloseSidebar
        closeModal={() => setCloseSidebarModalActive(false)}
        onCloseSidebar={onSidebarCloseClick}
      />
    </Dialog>
  );

  const revertChangesModal = () => (
    <Dialog
      dataQA="revert-changes-dialog"
      isOpen={isRevertModalActive}
      onClose={() => setIsRevertModalActive(false)}
      footer={false}
    >
      <RevertChanges
        closeModal={() => setIsRevertModalActive(false)}
        onRevertChanges={handleRevertAllChanges}
      />
    </Dialog>
  );

  const temporarilyCloseModal = () => (
    <Dialog
      dataQA="temporarily-close-dialog"
      isOpen={isTemporarilyCloseModalActive}
      onClose={() => setIsTemporarilyCloseModalActive(false)}
      footer={false}
    >
      <TemporarilyClose
        featureName={data.label?.name?.[0].text}
        closeModal={() => setIsTemporarilyCloseModalActive(false)}
        onTemporarilyClose={() => handleOpenAndClose("closed")}
      />
    </Dialog>
  );

  const reOpenModal = () => (
    <Dialog
      dataQA="reopen-dialog"
      isOpen={isReopenModalActive}
      onClose={() => setIsReopenModalActive(false)}
      footer={false}
    >
      <Reopen
        featureName={data.label?.name?.[0].text}
        closeModal={() => setIsReopenModalActive(false)}
        onReopen={() => handleOpenAndClose("open")}
      />
    </Dialog>
  );

  const featureDeleteModal = () => (
    <Dialog
      dataQA="delete-feature-dialog"
      isOpen={isDeleteFeatureModalActive}
      onClose={() => setIsDeleteFeatureModalActive(false)}
      footer={false}
    >
      <DeleteFeature
        featureName={data.label?.name?.[0].text}
        closeModal={() => setIsDeleteFeatureModalActive(false)}
        onDelete={handleFeatureDelete}
      />
    </Dialog>
  );

  const unarchiveModal = () => (
    <Dialog
      dataQA="unarchive-dialog"
      isOpen={isUnarchiveModalActive}
      onClose={() => setIsUnarchiveModalActive(false)}
      footer={false}
    >
      <Unarchive
        closeModal={() => setIsUnarchiveModalActive(false)}
        onUnarchive={handleUnarchiveFeature}
      />
    </Dialog>
  );

  const operatingHoursModal = () => (
    <Dialog
      dataQA="operating-hours-dialog"
      isOpen={isOperatingHoursModalActive}
      onClose={() => setIsOperatingHoursModalActive(false)}
      footer={false}
    >
      <OperatingHours
        times={times}
        operatingHours={data.information?.operating_hours}
        closeModal={() => setIsOperatingHoursModalActive(false)}
        onOperatingHoursUpdate={handleOperatingHoursUpdate}
      />
    </Dialog>
  );

  const deleteImageModal = () => (
    <Dialog
      dataQA="delete-image-dialog"
      isOpen={deleteImageModalState.isActive}
      onClose={() =>
        setDeleteImageModalState({
          isActive: false,
          file: null,
        })
      }
      footer={false}
    >
      <DeleteImage
        closeModal={() =>
          setDeleteImageModalState({
            isActive: false,
            file: null,
          })
        }
        onDelete={handleFileDelete}
      />
    </Dialog>
  );

  const isRevertable =
    props.status === STATUS.DRAFT_ARCHIVED ||
    props.status === STATUS.DRAFT_RESTORED ||
    props.status === STATUS.DRAFT;

  return (
    <Sidebar dataQA="edit-feature-sidebar" isOpen={true} position="relative">
      <div className={styles.container}>
        <div className={styles.sidebarTop}>
          <div className={styles.pillWrapper}>
            {data.is_temporarily_closed && (
              <Pill dataQA="temporarily-closed-pill" type="error">
                {t("edit_views.features.status.temporarily_closed")}
              </Pill>
            )}
            {data.status !== STATUS.PUBLISHED &&
              data.status !== STATUS.ARCHIVED && (
                <Pill dataQA="unpublished-pill" type="warning">
                  {t("edit_views.features.status.unpublished")}
                </Pill>
              )}
          </div>
          <div className={styles.tabs}>
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`${styles.tab} ${
                  tab === activeTab ? styles.active : ""
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {t(`edit_views.features.tabs.${tab}`)}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.sidebarMiddle}>{tabContent}</div>
        <div className={styles.sidebarBottom}>
          <Button
            dataQA="revert-changes-button"
            onClick={() => setIsRevertModalActive(true)}
            disabled={!isRevertable}
            size="regular"
            style="outlined"
            text={t("btn_labels.revert_changes")}
            type="primary"
          />
          <div className={styles.saveCancelButtonContainer}>
            <Button
              dataQA="cancel-button"
              size="regular"
              style="filled"
              text={t("btn_labels.cancel")}
              type="primary"
              disabled={!dataHasChanged}
              onClick={handleRevertAllChanges}
            />
            <Button
              dataQA="save-button"
              size="regular"
              style="filled"
              text={t("btn_labels.save")}
              type="location"
              onClick={() => onEditFeatureSave(data)}
              disabled={!dataHasChanged || hasValidationErrors}
            />
          </div>
        </div>
      </div>
      {revertChangesModal()}
      {temporarilyCloseModal()}
      {reOpenModal()}
      {featureDeleteModal()}
      {operatingHoursModal()}
      {deleteImageModal()}
      {closeSidebarModal()}
      {unarchiveModal()}
    </Sidebar>
  );
};

export default EditFeatureSidebar;
